import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import moment from 'src/app/utils/moment';
import { Share } from '@capacitor/share';
import { Directory, Filesystem } from '@capacitor/filesystem';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  index: number = 0;
  workout: any;
  workoutProgram: any;
  workoutProgramID: string;
  workoutArryId: number;
  lastTracked: any;
  workoutProgramTitle: string = '';

  isShown = true;

  constructor(
    private alertController: AlertController,
  ) { }

  date(datecreated: string, isComment = false) {
    let createdDate = new Date(datecreated)
    let temp = createdDate.toString().split(' ')
    let month = temp[1];
    let date = temp[2];
    let year = temp[3];
    if (!isComment)
      return date + ' ' + month;
    else
      return month + ' ' + date;
  }

  calculateAge(birthday: string) {
    return (moment as any)().diff((moment as any)(birthday, "YYYY-MM-DD"), "years", false);
  }

  async alert(message: string) {
    const alert = await this.alertController.create({
      message: message,
      buttons: ['OK']
    });
    await alert.present();
  }

  //Shared Service
  setWorkoutProgramID(workoutProgramID) {
    this.workoutProgramID = workoutProgramID;
  }

  getWorkoutProgramID() {
    return this.workoutProgramID;
  }

  setWorkoutProgram(workoutProgram) {
    this.workoutProgram = workoutProgram;
  }

  setWorkout(workout) {
    this.workout = workout;
  }

  getWorkout() {
    return this.workout;
  }

  setWorkoutArrId(id) {
    this.workoutArryId = id;
  }

  getWorkoutArrId() {
    return this.workoutArryId;
  }

  setworkoutProgramTitle(title) {
    this.workoutProgramTitle = title;
  }

  getworkoutProgramTitle() {
    return this.workoutProgramTitle;
  }

  setLastTracked(workout) {
    this.lastTracked = workout;
  }
  getLastTracked() {
    return this.lastTracked;
  }

  roundNumber(value: number, decimalPlaces: number) {
    var multiplier = Math.pow(10, decimalPlaces);
    return Math.round(value * multiplier) / multiplier;
  }

  copyObject(toCopy) {
    return JSON.parse(JSON.stringify(toCopy))
  }

  transformFirebaseObjectToArray(obj) {
    let results: any[] = []
    for (const id in obj) {
      if (obj.hasOwnProperty(id)) {
        let item = obj[id];
        item.id = id;
        item.uid = id;
        results.push(item);
      }
    }
    return results
  }

  cleanObjectForFirebase(obj: any) {
    const toClean = this.copyObject(obj);
    delete toClean.uid;
    delete toClean.id;
    delete toClean.$id;
    return toClean;
  }

  getMinutesFromDuration(quantity: number, unit: "minutes" | "hours"): number {
    if (unit === "hours") {
      return quantity * 60;
    } else {
      return quantity
    }
  }

  getFeetInches(inches: number) {
    return {
      feet: Math.floor(inches / 12),
      inches: inches % 12,
    }
  }

  nearestMinutes(interval: number, someMoment: moment.Moment): moment.Moment {
    const roundedMinutes = Math.round(someMoment.clone().minute() / interval) * interval;
    return someMoment.clone().minute(roundedMinutes).second(0);
  }

  nearestPastMinutes(interval: number, someMoment: moment.Moment): moment.Moment {
    const roundedMinutes = Math.floor(someMoment.minute() / interval) * interval;
    return someMoment.clone().minute(roundedMinutes).second(0);
  }

  nearestFutureMinutes(interval: number, someMoment: moment.Moment): moment.Moment {
    const roundedMinutes = Math.ceil(someMoment.minute() / interval) * interval;
    return someMoment.clone().minute(roundedMinutes).second(0).milliseconds(0);
  }

  blobToBase64(file: Blob): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  pdfToBlob(pdfObj): Promise<Blob> {
    return new Promise((res, rej) => {
      pdfObj.getBlob((blob) => res(blob));
    });
  }

  downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }

  async openOrSharePdf(pdfObj, fileName: string, isMobile: boolean) {
    const blob = await this.pdfToBlob(pdfObj);

    if (isMobile) {
      const dataUrl = await this.blobToBase64(blob);
      const base64Data = (dataUrl as string).split(',')[1];

      await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });

      const { uri } = await Filesystem.getUri({
        path: fileName,
        directory: Directory.Cache
      });

      await Share.share({ url: uri, dialogTitle: 'Share' });
      return;
    }

    this.downloadBlob(blob, fileName);
  }

  async getBase64ImageFromUrl(imageUrl) {
    var res = await fetch(imageUrl);
    var blob = await res.blob();
    return new Promise((resolve, reject) => {
      var reader  = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result);
      };
  
      reader.onerror = (error) => {
        return reject(error);
      };
    
      reader.readAsDataURL(blob);
    })
  }

  bodyTypeToValue(bodyType: string) {
    switch (bodyType) {
      case "ectomorph":
        return 1.15
      case "mesomorph":
        return 1
      case "endomorph":
        return 0.85
      default:
        return null
    }
  }

  activityLevelToValue(AL: string) {
    switch (AL) {
      case "sedentary":
        return 1.2
      case "lightlyActive":
        return 1.375
      case "moderatelyActive":
        return 1.465
      case "active":
        return 1.55
      case "veryActive":
        return 1.75
      case "superActive":
        return 1.9
      default:
        return null
    }
  }

  activityValueToLevel(AL: number) {
    switch (AL) {
      case 1.2:
        return "sedentary"
      case 1.375:
        return "lightlyActive"
      case 1.465:
        return "moderatelyActive"
      case 1.55:
        return "active"
      case 1.75:
        return "veryActive"
      case 1.9:
        return "superActive"
      default:
        return null
    }
  }

  getTrialRemainingDays(trialEndDate: string) {
    return (moment as any)(trialEndDate).diff((moment as any)(), "days");
  }

  async downloadImage(url: string) {
    let res = await fetch(url)
    return res.blob();
  }

  getFileExtensionFromType(type: string) {
    if (type.indexOf("jpeg") > -1 || type.indexOf("jpg") > -1) {
      return ".jpg"
    }

    if (type.indexOf("png") > -1 || type.indexOf("png") > -1) {
      return ".png"
    }

    return "";
  }

  findObjectParent(object, val) {
    let toReturn;
    if(object) {
      for (const item of object) {
        if(item.value === val) {
          toReturn = item;
          break;
        }

        if(item.children) {
          toReturn = this.findObjectParent(item.children, val);
          if(toReturn) {
            break
          }
        }
      }
    }
    return toReturn
  }

  getChildrenValues(item, toSearch): string[] {
    const toReturn = [];
    //const filter = _.find(toSearch, {value: item});
    const filter = this.findObjectParent(toSearch, item);
    if(filter?.children) {
      filter.children.forEach(element => {
        toReturn.push(element.value);
        toReturn.push(...this.getChildrenValues(element.value, toSearch));
      });
    }
    return toReturn
  }

  collapseChildren(items): any[] {
    const toReturn = [];
    for (const item of items) {
      toReturn.push(item);
      if(item.children) {
        toReturn.push(...this.collapseChildren(item.children));
      }
    } 
    return toReturn
  }


  getFileType(ext: any): "image" | "video" | "file" | "audio" {
    if(ext.indexOf("image") > -1) {
      return "image"
    } else if(ext.indexOf("video") > -1) {
      return "video"
    } else {
      return "file"
    }
  }

  valueExists(val) {
    return typeof val !== "undefined" && val !== null
  }

  valueIsNumber(val) {
    return typeof val == 'number'
  }

  dateRangeOverlaps(a_start, a_end, b_start, b_end) {
    const aStart = new Date(a_start).getTime();
    const aEnd = new Date(a_end).getTime();
    const bStart = new Date(b_start).getTime();
    const bEnd = new Date(b_end).getTime();

    // Guard invalid timestamps to avoid false-positive overlap checks.
    if ([aStart, aEnd, bStart, bEnd].some(value => Number.isNaN(value))) {
      return false;
    }

    // Compare as half-open ranges [start, end) to block true overlaps.
    return aStart < bEnd && bStart < aEnd;
  }

  getBase64ImageFromURL(url) {
    return new Promise((resolve, reject) => {
      var img = new Image();
      img.setAttribute("crossOrigin", "anonymous");

      img.onload = () => {
        var canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        var dataURL = canvas.toDataURL("image/png");

        resolve(dataURL);
      };

      img.onerror = error => {
        reject(error);
      };

      img.src = url;
    });
  }

  handlePromiseSuccess(res): Promise<any> {
    return Promise.resolve(res)
  }

  handlePromiseRejection(err): Promise<any> {
    if(err) {
      if(err.error) {
        return Promise.reject(err.error)
      } else {
        return Promise.reject(err);
      }
    } else {
      return Promise.reject({code: "unknown-error"});
    }
  }

  feetInchesToInches(feet: number, inches: number) {
    return feet * 12 + inches;
  }

  inchesToFeetInches(inches: number): {feet: number, inches: number} {
    let feetFromInches = Math.floor(inches / 12);//There are 12 inches in a foot
    let inchesRemainder = inches % 12;
 
    return {
      feet: feetFromInches,
      inches: inchesRemainder,
    }
  }

  lbsToKg(lbs) {
    const weightFactor = 0.454;
    return Math.round(lbs * weightFactor * 10) / 10
  }

  kgToLbs(lbs) {
    const weightFactor = 0.454;
    return Math.round(lbs / weightFactor * 10) / 10
  }

  inchesToCm(inches) {
    return this.roundNumber(inches * 2.54, 0);
  }

  cmToInches(cm) {
    return this.roundNumber(cm / 2.54, 0)
  }

  cmToFeetInches(cm: number): {feet: number, inches: number} {
    const inches = this.cmToInches(cm);
    let feetFromInches = Math.floor(inches / 12);//There are 12 inches in a foot
    let inchesRemainder = inches % 12;
 
    return {
      feet: feetFromInches,
      inches: inchesRemainder,
    }
  }

  feetInchesToCM(feet: number, inches: number): number {
    const inch = this.feetInchesToInches(feet, inches);
    return this.inchesToCm(inch);
  }

  pad(num, size) {
    num = num.toString();
    while (num.length < size) num = "0" + num;
    return num;
  }

  appVersionToNumber(version: string) {
    const arr = version.split(".");
    const joined = arr.join("").padEnd(4, "0");
    return parseInt(joined)
  }

  fromNow(date: string) {
    const diff = <any>new Date() - <any>(moment as any)(date).toDate();
    var seconds = Math.floor((diff) / 1000);
    var years = Math.floor(seconds / 31536000);
    var months = Math.floor(seconds / 2592000);
    var days = Math.floor(seconds / 86400);
  
    if (days > 548) {
      return years + " years ago";
    }
    if (days >= 320 && days <= 547) {
      return "a year ago";
    }
    if (days >= 45 && days <= 319) {
      return months + " months ago";
    }
    if (days >= 26 && days <= 45) {
      return "a month ago";
    }
  
    var hours = Math.floor(seconds / 3600);
  
    if (hours >= 36 && days <= 25) {
      return days + " days ago";
    }
    if (hours >= 22 && hours <= 35) {
      return "a day ago";
    }
  
    var minutes = Math.floor(seconds / 60);
  
    if (minutes >= 90 && hours <= 21) {
      return hours + " hours ago";
    }
    if (minutes >= 45 && minutes <= 89) {
      return "an hour ago";
    }
    if (seconds >= 90 && minutes <= 44) {
      return minutes + " minutes ago";
    }
    if (seconds >= 45 && seconds <= 89) {
      return "a minute ago";
    }
    if (seconds >= 0 && seconds <= 45) {
      return "a minute ago";
    }
  }

  fromNowAbbr(date: string) {
    const diff = <any>new Date() - <any>(moment as any)(date).toDate();
    var seconds = Math.floor((diff) / 1000);
    var years = Math.floor(seconds / 31536000);
    var months = Math.floor(seconds / 2592000);
    var days = Math.floor(seconds / 86400);
  
    if (days > 548) {
      return "> 1 yr ago ";
    }
    if (days >= 320 && days <= 547) {
      return "1 yr ago";
    }
    if (days >= 45 && days <= 319) {
      return months + " mths ago";
    }
    if (days >= 26 && days <= 45) {
      return "1 mth ago";
    }
  
    var hours = Math.floor(seconds / 3600);
  
    if (hours >= 48 && days <= 25) {
      return days + " days ago";
    }
    if (hours >= 22 && hours <= 47) {
      return "1 day ago";
    }
  
    var minutes = Math.floor(seconds / 60);
  
    if (minutes >= 90 && hours <= 21) {
      return hours + " hrs ago";
    }
    if (minutes >= 45 && minutes <= 89) {
      return "1 hr ago";
    }
    if (seconds >= 90 && minutes <= 44) {
      return minutes + " mins ago";
    }
    if (seconds >= 45 && seconds <= 89) {
      return "1 min ago";
    }
    if (seconds >= 0 && seconds <= 45) {
      return "1 min ago";
    }
  }

  moveArrayItem(arr, fromIndex, toIndex) {
      var element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
  }

  clearObjectProperties(obj) {
    for (const prop of Object.getOwnPropertyNames(obj)) {
      delete obj[prop];
    }
  }

  sanitizeFileName(name: string): string {
    return name
      .replace(/[/\\?%*:|"<>]/g, '-')    // Replace illegal characters with a dash
      .replace(/\s+/g, '_')              // Optionally replace spaces with underscores
      .trim();
  }

  htmlToPdfMake(html) {
    const div = document.createElement('div');
    div.innerHTML = html;

    const pdfContent = [];
    
    Array.from(div.childNodes).forEach(node => {
        if (node.nodeName === 'P') {
            pdfContent.push({ text: node.textContent, margin: [0, 5] }); // Add margin to separate paragraphs
        } else if (node.nodeName === 'OL' || node.nodeName === 'UL') {
            // Process lists
            const listItems = [];
            Array.from(node.childNodes).forEach(listItem => {
                if (listItem.nodeName === 'LI') {
                    listItems.push({ text: listItem.textContent, margin: [0, 5] });
                }
            });
            pdfContent.push({ ul: listItems }); // Using unordered list formatting for pdfMake
        } else if (node.nodeName === 'BR') {
            pdfContent.push({ text: '', margin: [0, 5] }); // Line break
        } else {
            // Process other text nodes
            pdfContent.push(node.textContent);
        }
    });

    return pdfContent;
  }
}
